// src/config/plotConfigs/comparisonProfileConfig.js

export const comparisonProfileConfig = {
  sections: [
    {
      fields: [
        {
          id: "transcript",
          label: "Gene/Transcript",
          type: "text",
          placeholder: "Enter gene or transcript ID",
          tooltip:
            "The gene or transcript to be plotted. If a gene is entered you will be presented with a list of all transcripts associated with that gene (unless only a single transcript exists). One of the transcripts shown will be designated the 'principal' isoform, this will either be based on the transcript with the longest CDS or based on APPRIS annotations, the method used to determine the principal isoform is shown in brackets in the header of the 'Type' column.",
          required: true,
          width: 6,
          mode: "single", // ✅ Only show in single mode
        },
        {
          id: "minRead",
          label: "Min Read",
          type: "number",
          placeholder: "20",
          min: 1,
          max: 1000,
          tooltip:
            "The minimum read length to be displayed on the graph. Any reads below this length will be ignored.",
          width: 3,
        },
        {
          id: "maxRead",
          label: "Max Read",
          type: "number",
          placeholder: "150",
          min: 1,
          max: 1000,
          tooltip:
            "The maximum read length to be displayed on the graph. Any reads above this length will be ignored.",
          width: 3,
        },

        // ✅ ADVANCED SETTINGS - These will be shown in modal
        {
          id: "highlightStart",
          label: "Highlight start",
          type: "text",
          placeholder: "Enter comma-separated numbers (e.g., 10,80)",
          tooltip:
            "Highlight start/Highlight stop can be used to highlight a specific region of a transcript. Filling in a value for highlight start and highlight stop will colour in that region of the plot. Multiple regions can be highlighted by entering a comma seperated list of numbers, for example to highlight the regions between 10-20 nts and 80-90 nts, enter 10,80 in the Highlight start input box and 20,90 in the Highlight stop input box.",
          width: 6,
          mode: "single",
          advanced: true,
        },
        {
          id: "highlightStop",
          label: "Highlight stop",
          type: "text",
          placeholder: "Enter comma-separated numbers (e.g., 20,90)",
          tooltip:
            "Highlight start/Highlight stop can be used to highlight a specific region of a transcript. Filling in a value for highlight start and highlight stop will colour in that region of the plot. Multiple regions can be highlighted by entering a comma seperated list of numbers, for example to highlight the regions between 10-20 nts and 80-90 nts, enter 10,80 in the Highlight start input box and 20,90 in the Highlight stop input box.",
          width: 6,
          mode: "single",
          advanced: true,
        },
        {
          id: "riboSeqCoverage",
          label: "Ribo-Seq coverage",
          type: "checkbox",
          tooltip:
            "If checked, every position of every read contributes to the data displayed on the plot, if unchecked only the inferred a-site of the read contributes to the plot.",
          width: 12,
          advanced: true,
        },
        {
          id: "ambig_check",
          label: "Allow ambiguous reads",
          type: "checkbox",
          tooltip:
            "Checking this box means that both ambiguously mapped reads and unambiguously mapped reads will be displayed. A read is considered ambiguous if it maps to 2 or 3 different genes. Ambiguous reads are weighted according to the number of genes they align to, reads that align to two genes have a count of 0.5 while reads that align to 3 genes have a count of 0.33.",
          width: 3,
          mode: "batch",
          advanced: true,
        },
        {
          id: "norm_check",
          label: "Normalize by mapped reads per seq type",
          type: "checkbox",
          tooltip:
            "When looking at the difference between two groups of files there may be a large difference in the number of mapped reads in each group.Checking this option aims to mitigate the effect of mapped reads differences by determining the factor difference between the groups and then decreasing the counts of the higher groups by that factor. For example if there is one dataset in condition 1 with 1,000,000 mapped reads and one dataset in condition 2 with 500,000 mapped reads, the count for every position in condition 1 would be divided by 2.",
          width: 3,
          mode: "batch",
          advanced: true,
        },
        {
          id: "email",
          label: "Email notification",
          type: "checkbox",
          tooltip: "Send email when analysis is complete",
          mode: "batch", // Only show in batch mode
          width: 12,
        },

        {
          id: "normalizationType",
          label: "Normalization Method",
          type: "radio",
          mode: "single",
          options: [
            {
              value: "no",
              label: "None",
              tooltip: "Do not perform profile counts normalization.",
            },
            {
              value: "by_type",
              label: "By mapped reads per seq type",
              tooltip:
                "Normalize profile counts by total reads mapped for each sequence type independently.",
            },
            {
              value: "by_all",
              label: "By mapped reads ignoring seq type",
              tooltip:
                "Normalize profile counts by total reads mapped ignoring sequence types.",
            },
            {
              value: "by_tran",
              label: "By reads mapped to the transcript",
              tooltip:
                "Normalize profile counts by number of reads mapped to the transcript.",
            },
          ],
          tooltip: "Choose the normalization method for profile counts.",
          width: 12,
          advanced: true, // ✅ Mark as advanced setting for modal
        },
      ],
    },
  ],
};

export const comparisonProfileDefaults = {
  riboSeqCoverage: true,
  transcript: "ENST00000316292", // Will be filtered out in batch mode
  minRead: 20,
  maxRead: 150,
  highlightStart: "0",
  highlightStop: "0",
  normalizationType: "by_type",
  email: true,
  plot_url: "/comparison/",
  norm_check: true,
  ambig_check: false,
};

export const comparisonProfileValidation = {
  transcript: {
    required: true,
    message: "Please enter a gene or transcript ID",
  },
  minRead: {
    min: 1,
    max: 100,
    message: "Min read must be between 1 and 100",
  },
  maxRead: {
    min: 1,
    max: 200,
    message: "Max read must be between 1 and 200",
  },
};
